/**
 * Écran `/inscription`.
 *
 * Le corps envoyé à `POST /auth/register` est validé par `registerInputSchema` : email normalisé,
 * mot de passe ≥ 10 caractères, pseudo 3–20 `[A-Za-z0-9_-]`, et surtout les deux consentements
 * obligatoires (`cguAccepted` / `privacyAccepted` en `z.literal(true)`).
 *
 * Le contrat d’inscription ne renvoie pas de jeton : l’écran enchaîne donc un `POST /auth/login`
 * avec les identifiants qui viennent d’être créés (voir `src/api/auth.ts`).
 */
import { registerInputSchema } from '@elemora/shared/api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { connecter, inscrire, useSessionStore, versErreurApi, type ErreurApi } from '../api';
import { localeCourante, synchroniserLangueUtilisateur } from '../i18n';
import { texteErreur, traduireIssues, type ErreursChamps } from './validation';
import { BandeauErreur } from '../ui/BandeauErreur';
import { Bouton } from '../ui/Bouton';
import { CaseACocher } from '../ui/CaseACocher';
import { Champ } from '../ui/Champ';
import { Entete } from '../ui/Entete';

import type { FormEvent } from 'react';

export function EcranInscription() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const definirSession = useSessionStore((etat) => etat.definirSession);

  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [pseudo, setPseudo] = useState('');
  const [consentementCgu, setConsentementCgu] = useState(false);
  const [consentementConfidentialite, setConsentementConfidentialite] = useState(false);
  const [erreurs, setErreurs] = useState<ErreursChamps>({});
  const [erreurApi, setErreurApi] = useState<ErreurApi | null>(null);
  const [enCours, setEnCours] = useState(false);

  async function soumettre(evenement: FormEvent<HTMLFormElement>): Promise<void> {
    evenement.preventDefault();
    const resultat = registerInputSchema.safeParse({
      email,
      password: motDePasse,
      pseudo,
      locale: localeCourante(),
      cguAccepted: consentementCgu,
      privacyAccepted: consentementConfidentialite,
    });
    if (!resultat.success) {
      setErreurs(
        traduireIssues(resultat.error, {
          email,
          password: motDePasse,
          pseudo,
          cguAccepted: consentementCgu,
          privacyAccepted: consentementConfidentialite,
        }),
      );
      setErreurApi(null);
      return;
    }
    setErreurs({});
    setErreurApi(null);
    setEnCours(true);
    try {
      await inscrire(resultat.data);
      const reponse = await connecter({
        email: resultat.data.email,
        password: resultat.data.password,
      });
      definirSession({
        user: reponse.user,
        progression: reponse.progression,
        accessToken: reponse.accessToken,
      });
      synchroniserLangueUtilisateur(reponse.user.locale);
      navigate('/', { replace: true });
    } catch (erreur) {
      setErreurApi(versErreurApi(erreur));
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="ecran ecran--centree">
      <Entete />
      <main className="panneau formulaire">
        <h1 className="formulaire__titre">{t('auth.inscription.titre')}</h1>
        <p className="formulaire__sous-titre">{t('auth.inscription.sousTitre')}</p>
        {erreurApi === null ? null : <BandeauErreur erreur={erreurApi} />}
        <form className="formulaire__corps" onSubmit={soumettre} noValidate>
          <Champ
            id="inscription-email"
            etiquette={t('auth.email')}
            type="email"
            autoComplete="email"
            value={email}
            disabled={enCours}
            onChange={(evenement) => setEmail(evenement.target.value)}
            erreur={texteErreur(erreurs, 'email', t)}
          />
          <Champ
            id="inscription-pseudo"
            etiquette={t('auth.pseudo')}
            type="text"
            autoComplete="username"
            value={pseudo}
            disabled={enCours}
            aide={t('auth.aidePseudo')}
            onChange={(evenement) => setPseudo(evenement.target.value)}
            erreur={texteErreur(erreurs, 'pseudo', t)}
          />
          <Champ
            id="inscription-mot-de-passe"
            etiquette={t('auth.motDePasse')}
            type="password"
            autoComplete="new-password"
            value={motDePasse}
            disabled={enCours}
            aide={t('auth.aideMotDePasse')}
            onChange={(evenement) => setMotDePasse(evenement.target.value)}
            erreur={texteErreur(erreurs, 'password', t)}
          />
          <CaseACocher
            id="inscription-cgu"
            libelle={t('auth.consentementCgu')}
            checked={consentementCgu}
            disabled={enCours}
            onChange={(evenement) => setConsentementCgu(evenement.target.checked)}
            erreur={texteErreur(erreurs, 'cguAccepted', t)}
          />
          <CaseACocher
            id="inscription-confidentialite"
            libelle={t('auth.consentementConfidentialite')}
            checked={consentementConfidentialite}
            disabled={enCours}
            onChange={(evenement) => setConsentementConfidentialite(evenement.target.checked)}
            erreur={texteErreur(erreurs, 'privacyAccepted', t)}
          />
          <Bouton type="submit" enCours={enCours} texteEnCours={t('auth.inscription.enCours')}>
            {t('auth.inscription.valider')}
          </Bouton>
        </form>
        <p className="formulaire__lien">
          <Link to="/connexion">{t('auth.inscription.versConnexion')}</Link>
        </p>
        <p className="formulaire__decouvrir">
          <Link to="/decouvrir">{t('auth.versDecouvrir')}</Link>
        </p>
      </main>
    </div>
  );
}
